import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiCollapseDiagonalLine } from "react-icons/ri";
import { IoIosArrowDown, IoIosArrowUp, IoIosArrowForward } from "react-icons/io";
import { FaFilter } from "react-icons/fa";
import { TbReload, TbArrowsSort } from "react-icons/tb";
import { MdOutlineAdd } from "react-icons/md";
import Addgroup from './addgroup';

const API_URL = process.env.REACT_APP_API_BASE;

function GroupList() {
    const navigate = useNavigate();
    const [showfilter, setshowfilter] = useState(false);
    const [filterGroupName, setFilterGroupName] = useState('');
    const [addgroupname, setaddgroupname] = useState('');
    const [addgroupdesc, setaddgroupdesc] = useState('');
    const [editingGroup, setEditingGroup] = useState(null); // { id, group_name, description }
    const [open, setopen] = useState(false);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);

    // ── Fetch all groups (with optional name filter) ──────────────────────────
    async function fetchGroups(name = '') {
        setLoading(true);
        try {
            const url = name
                ? `${API_URL}/auth/groups?name=${encodeURIComponent(name)}`
                : `${API_URL}/auth/groups`;
            const res = await fetch(url);
            const data = await res.json();
            setGroups(data);
        } catch (err) {
            console.error('Error fetching groups:', err);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchGroups();
    }, []);

    // ── Filter / Clear ────────────────────────────────────────────────────────
    function applyFilter() {
        fetchGroups(filterGroupName);
    }

    function clearFilter() {
        setFilterGroupName('');
        fetchGroups('');
    }

    // ── Open Create modal ─────────────────────────────────────────────────────
    function openCreateModal() {
        setEditingGroup(null);
        setaddgroupname('');
        setaddgroupdesc('');
        setopen(true);
    }

    // ── Open Edit modal ───────────────────────────────────────────────────────
    function openEditModal(group) {
        setEditingGroup(group);
        setaddgroupname(group.group_name);
        setaddgroupdesc(group.description || '');
        setopen(true);
    }

    // ── Save (Create or Update) ───────────────────────────────────────────────
    async function saveGroup() {
        if (!addgroupname.trim()) {
            alert('Group name is required');
            return;
        }

        try {
            if (editingGroup) {
                // UPDATE
                await fetch(`${API_URL}/auth/groups/${editingGroup.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        group_name: addgroupname.trim(),
                        description: addgroupdesc.trim() || null,
                    }),
                });
            } else {
                // CREATE — pass user_id from localStorage/context as needed
                const user_id = localStorage.getItem('user_id') || 1;
                await fetch(`${API_URL}/auth/groups`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id,
                        group_name: addgroupname.trim(),
                        description: addgroupdesc.trim() || null,
                    }),
                });
            }
        } catch (err) {
            console.error('Error saving group:', err);
        } finally {
            setopen(false);
            setEditingGroup(null);
            setaddgroupname('');
            setaddgroupdesc('');
            fetchGroups();
        }
    }

    // ── Delete group ──────────────────────────────────────────────────────────
    async function deleteGroup(id) {
        if (!window.confirm('Delete this group and all its contacts?')) return;
        try {
            await fetch(`${API_URL}/auth/groups/${id}`, { method: 'DELETE' });
            fetchGroups();
        } catch (err) {
            console.error('Error deleting group:', err);
        }
    }

    // ── Navigate to Manage Contacts page ──────────────────────────────────────
    function manageContacts(id) {
        navigate(`/groups/${id}/contacts`);
    }

    // ── Export CSV ────────────────────────────────────────────────────────────
    function exportCsv(id) {
        window.location.href = `${API_URL}/auth/export/csv?groupId=${id}`;
    }

    // ── Import (stub — wire your import page route) ───────────────────────────
    function importGroup(id) {
        navigate(`/groups/${id}/import`);
    }

    return (
        <>
            <div className='dark:bg-[#121519] bg-[#fff] min-h-screen w-[100%] px-4'>

                {/* ── Page Header ── */}
                <div className='border-b-[1px] border-[#cfcdcd] pb-1'>
                    <h1 className='text-[#000] font-semibold text-[20px] flex gap-1 items-center dark:text-[#fff]'>
                        Group List
                    </h1>
                    <div className='text-[#454546] dark:text-[#fff] text-[12px] font-semibold flex items-center cursor-pointer'>
                        <span>Home</span>
                        <IoIosArrowForward />
                        <span>Group List</span>
                    </div>
                </div>

                {/* ── Add Group Button ── */}
                <button
                    onClick={openCreateModal}
                    className='hover:bg-[#316bb0] border-[1px] border-[#316bb0] flex items-center text-[11px] hover:text-[#fff] text-[#316bb0] font-bold p-2 gap-1 rounded-[5px] mt-4'
                >
                    <MdOutlineAdd className='text-[14px]' />
                    Add Group
                </button>

                {/* ── Filter Bar ── */}
                <div
                    onClick={() => setshowfilter(!showfilter)}
                    className='w-[100%] flex justify-between px-4 py-2 bg-[#e0e0e04b] rounded-t-[20px] mt-4 cursor-pointer'
                >
                    <div className='flex items-center text-[#576071] font-semibold gap-3 dark:text-[#fff]'>
                        <RiCollapseDiagonalLine />
                        Filter Records
                    </div>
                    <div className='flex items-center text-[#576071] font-semibold gap-3 dark:text-[#fff]'>
                        {showfilter ? <IoIosArrowDown /> : <IoIosArrowUp />}
                    </div>
                </div>

                {showfilter && (
                    <div className='bg-[#edf1f5] py-2 px-4 w-[100%] dark:bg-[#121519]' onClick={e => e.stopPropagation()}>
                        <div className='w-[100%] flex flex-wrap gap-4'>
                            <div className='w-full sm:w-[48%] lg:w-[24%] flex flex-col gap-1'>
                                <label className='text-[#000] text-[12px] font-semibold dark:text-[#fff]'>Group Name</label>
                                <input
                                    value={filterGroupName}
                                    onChange={e => setFilterGroupName(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && applyFilter()}
                                    className='text-[#454546] border-[#d4cfcf] border-[1px] rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'
                                    type="text"
                                    placeholder='Search group name...'
                                />
                            </div>
                            <div className='w-full sm:w-[48%] lg:w-[24%] flex items-center gap-1 mt-4'>
                                <button
                                    onClick={applyFilter}
                                    className='flex items-center justify-center border-[1px] border-[#37715d] rounded-lg p-2 text-[#37715d] gap-2 hover:bg-[#37715d] hover:text-[#fff] w-full sm:w-auto'
                                >
                                    <FaFilter /> <span className='text-[12px] font-bold'>Filter</span>
                                </button>
                                <button
                                    onClick={clearFilter}
                                    className='flex items-center justify-center border-[1px] border-[#3f7fca] rounded-lg p-2 text-[#3f7fca] gap-2 hover:bg-[#3f7fca] hover:text-[#fff] w-full sm:w-auto'
                                >
                                    <TbReload /> <span className='text-[12px] font-bold'>Clear Filter</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Group Table ── */}
                <table className="w-full overflow-hidden border-[1px] border-[#dee2e6]">
                    <thead>
                        <tr className="text-left text-sm font-semibold text-gray-700">
                            <th className="p-2 cursor-pointer border-[1px] border-[#dee2e6]">
                                <div className="flex justify-between items-center gap-2">
                                    <span className='text-[11px] text-[#576071] dark:text-[#fff]'>Group Name</span>
                                    <button type="button" className="text-gray-500 hover:text-black"><TbArrowsSort /></button>
                                </div>
                            </th>
                            <th className="p-2 cursor-pointer border-[1px] border-[#dee2e6]">
                                <div className="flex justify-between items-center gap-2">
                                    <span className='text-[11px] text-[#576071] dark:text-[#fff]'>Total Contacts</span>
                                    <button type="button" className="text-gray-500 hover:text-black"><TbArrowsSort /></button>
                                </div>
                            </th>
                            <th className="p-2 border-[1px] border-[#dee2e6]">
                                <span className='text-[11px] text-[#576071] dark:text-[#fff]'>Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={3} className="p-3 text-center text-[12px] text-gray-400">Loading...</td>
                            </tr>
                        ) : groups.length === 0 ? (
                            <tr className="text-center text-gray-500 bg-[#f6f9fc] dark:bg-[#121519]">
                                <td colSpan={3} className="p-3 text-[11px]">No data found</td>
                            </tr>
                        ) : (
                            groups.map(group => (
                                <tr key={group.id} className="text-[12px] border-b hover:bg-[#f7fafc] dark:hover:bg-[#1a1f26]">
                                    <td className="p-2 font-medium">{group.group_name}</td>
                                    <td className="p-2">{group.total_contacts ?? 0}</td>
                                    <td className="p-2">
                                        <div className="flex gap-2 flex-wrap">
                                            {/* Manage Contacts */}
                                            <button
                                                onClick={() => manageContacts(group.id)}
                                                title="Manage Contacts"
                                                className="border px-2 py-1 rounded hover:bg-gray-100 text-[13px]"
                                            >📇</button>

                                            {/* Import */}
                                            <button
                                                onClick={() => importGroup(group.id)}
                                                title="Import Contacts"
                                                className="border px-2 py-1 rounded hover:bg-gray-100 text-[13px]"
                                            >⬆️</button>

                                            {/* Export CSV */}
                                            <button
                                                onClick={() => exportCsv(group.id)}
                                                title="Export CSV"
                                                className="border px-2 py-1 rounded hover:bg-gray-100 text-[13px]"
                                            >⬇️</button>

                                            {/* Edit */}
                                            <button
                                                onClick={() => openEditModal(group)}
                                                title="Edit Group"
                                                className="border px-2 py-1 rounded text-blue-500 hover:bg-blue-50 text-[13px]"
                                            >✏️</button>

                                            {/* Delete */}
                                            <button
                                                onClick={() => deleteGroup(group.id)}
                                                title="Delete Group"
                                                className="border px-2 py-1 rounded text-red-500 hover:bg-red-50 text-[13px]"
                                            >🗑️</button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                <p className='text-[11px] text-[#3f7fc8] mt-2'>
                    Showing 1 to {groups.length} of {groups.length} records
                </p>
            </div>

            {/* ── Create / Edit Group Modal ── */}
            <Addgroup
                isOpen={open}
                onClose={() => { setopen(false); setEditingGroup(null); }}
                heading={editingGroup ? 'Edit Group' : 'Create Group'}
            >
                <div className='py-2 border-y-[1px] border-[#8b8a8a67] my-2 w-[100%]'>
                    <div className='w-[100%] flex flex-col gap-3'>
                        <div className='flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-[#fff]'>
                                Group Name <span className='text-red-500'>*</span>
                            </label>
                            <input
                                value={addgroupname}
                                onChange={e => setaddgroupname(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border-[1px] rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'
                                type="text"
                                placeholder='Enter the group name'
                            />
                        </div>
                        <div className='flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-[#fff]'>
                                Description <span className='text-gray-400 font-normal'>(optional)</span>
                            </label>
                            <input
                                value={addgroupdesc}
                                onChange={e => setaddgroupdesc(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border-[1px] rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-[#fff]'
                                type="text"
                                placeholder='Optional description'
                            />
                        </div>
                    </div>
                </div>
                <div className='w-[100%] flex justify-end gap-1'>
                    {/* ✅ Fixed: was calling setopen instead of saveGroup */}
                    <button
                        onClick={saveGroup}
                        className='p-2 bg-[#00816a] rounded-md text-[12px] font-semibold text-[#fff]'
                    >
                        Save
                    </button>
                    <button
                        onClick={() => { setopen(false); setEditingGroup(null); }}
                        className='p-2 bg-[#d3dde6] rounded-md text-[12px] font-semibold text-[#555555]'
                    >
                        Close
                    </button>
                </div>
            </Addgroup>
        </>
    );
}

export default GroupList;